import React, { Component } from "react";
import {
  loading,
  NavbarAdmin,
  GET,
  format_date,
  DateRangePicker,
  t // นำเข้าฟังก์ชัน t
} from "../../components/CustomComponent.js";

const MONTH_SHORT_KEYS = ["jan_short", "feb_short", "mar_short", "apr_short", "may_short", "jun_short", "jul_short", "aug_short", "sep_short", "oct_short", "nov_short", "dec_short"];
const CATEGORY_COLORS = ["#a855f7", "#3b82f6", "#f97316", "#22c55e", "#ef4444", "#eab308", "#06b6d4", "#64748b"];

export default class PMCompliance extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data_work_orders: [],
      data_plans: [],
      data_schedule: [],
      data_assets: [],
      start_date: this.getDefaultStartDate(),
      end_date: this.getDefaultEndDate(),
    };
  }

  async componentDidMount() {
    loading(true);
    const currentYear = new Date().getFullYear();
    const [woResult, planResult, scheduleThisYear, scheduleNextYear, assetResult] = await Promise.all([
      GET("admin/work-orders", { page: 0 }),
      GET("admin/inspection-plans", { page: 0 }),
      GET("admin/inspection-plans/schedule", { year: currentYear }),
      GET("admin/inspection-plans/schedule", { year: currentYear + 1 }),
      GET("admin/assets", { page: 0 }),
    ]);

    this.setState({
      data_work_orders: woResult?.status ? woResult.data || [] : [],
      data_plans: planResult?.status ? planResult.data || [] : [],
      data_schedule: [
        ...(scheduleThisYear?.status ? scheduleThisYear.data?.data || [] : []),
        ...(scheduleNextYear?.status ? scheduleNextYear.data?.data || [] : []),
      ],
      data_assets: assetResult?.status ? assetResult.data || [] : [],
    });
    loading(false);
  }

  getDefaultStartDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  getDefaultEndDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  toDateOnly = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  // Helper สำหรับแปลคำพื้นฐานใน Mock Data เช่น Status, Frequency
  getTranslateMockData = (text) => {
    if (!text) return "";
    const key = text.toLowerCase().replace(" ", "_");
    return t(key) || text;
  };

  // PM work-order = ใบสั่งงานที่ถูกสร้างจากแผนการตรวจ (inspection plan) — นิยามเดียวกับ pm-dashboard.service.js
  getPmWorkOrders = () => (this.state.data_work_orders || []).filter((wo) => wo?.inspection_plan_id);

  // วันที่มีผลของใบสั่งงาน = วัน visit แรกสุด ถ้าไม่มีใช้ start_date ของ WO (เหมือน pm-dashboard.service.js)
  getEffectiveDate = (wo) => {
    const visitDates = (wo.visits || [])
      .map((v) => v.start_date)
      .filter(Boolean)
      .map((d) => new Date(d))
      .filter((d) => !isNaN(d.getTime()));
    if (visitDates.length) return new Date(Math.min(...visitDates.map((d) => d.getTime())));
    return wo.start_date ? new Date(wo.start_date) : null;
  };

  getPmBucket = (wo) => {
    const status = String(wo?.status || "draft").toLowerCase();
    if (["completed", "success"].includes(status)) return "completed";
    if (status === "open") return "in_progress";
    const eff = this.toDateOnly(this.getEffectiveDate(wo));
    const today = this.toDateOnly(new Date());
    if (eff && today && eff < today) return "overdue";
    return "scheduled";
  };

  getFilteredPmWorkOrders = () => {
    const { start_date, end_date } = this.state;
    const rangeStart = this.toDateOnly(start_date);
    const rangeEnd = this.toDateOnly(end_date || start_date);
    return this.getPmWorkOrders().filter((wo) => {
      const eff = this.toDateOnly(this.getEffectiveDate(wo));
      if (!eff || !rangeStart || !rangeEnd) return false;
      return eff >= rangeStart && eff <= rangeEnd;
    });
  };

  getAssetCategoryMap = () => {
    const map = new Map();
    (this.state.data_assets || []).forEach((asset) => {
      map.set(asset.uid, asset?.part?.part_category?.name || "-");
    });
    return map;
  };

  getAssetLabel = (wo) => wo?.asset?.part?.name || wo?.asset?.asset_code || wo?.asset?.serial_number || "-";

  getTechnicianLabel = (wo) => {
    for (const visit of wo.visits || []) {
      for (const vt of visit.visits_technicians || []) {
        const tech = vt?.technician;
        if (tech) {
          return (
            tech.nickname_th ||
            tech.nickname_en ||
            [tech.first_name_th, tech.last_name_th].filter(Boolean).join(" ") ||
            tech.technician_code ||
            "-"
          );
        }
      }
    }
    return "-";
  };

  // PM ที่เลยกำหนดอยู่ ณ ปัจจุบัน (ไม่จำกัดตามช่วงวันที่ที่เลือก เหมือน "under_maintenance" ของ asset-report.js)
  getOverdueList = () => {
    const today = this.toDateOnly(new Date());
    return this.getPmWorkOrders()
      .filter((wo) => this.getPmBucket(wo) === "overdue")
      .map((wo) => {
        const eff = this.getEffectiveDate(wo);
        const effOnly = this.toDateOnly(eff);
        const daysOverdue = effOnly && today ? Math.round((today - effOnly) / 86400000) : 0;
        return {
          uid: wo.uid,
          task: wo?.checklist_template?.name || t("pm_task"),
          asset: this.getAssetLabel(wo),
          due: eff ? format_date(eff, "dd/mm/yyyy", "th") : "-",
          daysOverdue,
          tech: this.getTechnicianLabel(wo),
        };
      })
      .sort((a, b) => b.daysOverdue - a.daysOverdue)
      .slice(0, 10);
  };

  // จับคู่ plan_uid -> ชุด wo_uid จาก schedule (แก้ปัญหา work_orders.inspection_plan_id เป็น internal id ที่ frontend ไม่รู้จัก)
  buildPlanWoUidMap = () => {
    const map = new Map();
    (this.state.data_schedule || []).forEach((row) => {
      const set = map.get(row.plan_uid) || new Set();
      (row.schedule || []).forEach((entry) => {
        if (entry.wo_uid) set.add(entry.wo_uid);
      });
      map.set(row.plan_uid, set);
    });
    return map;
  };

  getPlanSummary = (plan, planWoUidMap, woByUid) => {
    const woUids = planWoUidMap.get(plan.uid) || new Set();
    const relatedWos = [...woUids].map((uid) => woByUid.get(uid)).filter(Boolean);

    let lastCompletedDate = null;
    let nextDueDate = null;
    let hasOverdue = false;

    relatedWos.forEach((wo) => {
      const bucket = this.getPmBucket(wo);
      const eff = this.getEffectiveDate(wo);
      if (!eff) return;

      if (bucket === "completed") {
        if (!lastCompletedDate || eff > lastCompletedDate) lastCompletedDate = eff;
      } else {
        if (bucket === "overdue") hasOverdue = true;
        if (!nextDueDate || eff < nextDueDate) nextDueDate = eff;
      }
    });

    let status = "on_track";
    if (hasOverdue) {
      status = "overdue";
    } else if (nextDueDate) {
      const daysUntilDue = Math.round((this.toDateOnly(nextDueDate) - this.toDateOnly(new Date())) / 86400000);
      if (daysUntilDue <= 7) status = "due_soon";
    }

    return { lastCompletedDate, nextDueDate, status };
  };

  getTaskSummaryRows = () => {
    const planWoUidMap = this.buildPlanWoUidMap();
    const woByUid = new Map((this.state.data_work_orders || []).map((wo) => [wo.uid, wo]));
    const categoryMap = this.getAssetCategoryMap();
    const statusStyle = {
      on_track: "bg-success-light text-success border-success",
      due_soon: "bg-warning-light text-warning border-warning",
      overdue: "bg-danger-light text-danger border-danger",
    };

    return (this.state.data_plans || [])
      .filter((plan) => plan.is_active !== false)
      .map((plan) => {
        const summary = this.getPlanSummary(plan, planWoUidMap, woByUid);
        return {
          uid: plan.uid,
          task: plan.checklist_template?.name || t("pm_task"),
          frequency: this.getTranslateMockData(plan.frequency),
          category: categoryMap.get(plan.asset?.uid) || "-",
          last: summary.lastCompletedDate ? format_date(summary.lastCompletedDate, "dd/mm/yyyy", "th") : "-",
          next: summary.nextDueDate ? format_date(summary.nextDueDate, "dd/mm/yyyy", "th") : "-",
          status: summary.status,
          statusClass: statusStyle[summary.status],
        };
      });
  };

  getCategoryCounts = (pmWorkOrders) => {
    const categoryMap = this.getAssetCategoryMap();
    const counts = new Map();
    pmWorkOrders.forEach((wo) => {
      const label = categoryMap.get(wo?.asset?.uid) || "-";
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return [...counts.entries()]
      .map(([label, count], idx) => ({ label, count, color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length] }))
      .sort((a, b) => b.count - a.count);
  };

  buildDonutGradient = (categoryCounts, total) => {
    if (!total) return "#e9ecef";
    let cursor = 0;
    const stops = categoryCounts.map((c) => {
      const start = cursor;
      cursor += (c.count / total) * 100;
      return `${c.color} ${start}% ${cursor}%`;
    });
    return `conic-gradient(${stops.join(", ")})`;
  };

  // เทรนด์ % Compliance ย้อนหลัง 6 เดือน (ไม่ผูกกับช่วงวันที่ที่เลือก เพราะเทรนด์ต้องดูหลายเดือนเสมอ)
  getComplianceTrend = () => {
    const now = new Date();
    const pmWorkOrders = this.getPmWorkOrders();

    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ year: d.getFullYear(), month: d.getMonth(), label: t(MONTH_SHORT_KEYS[d.getMonth()]) });
    }

    return months.map((m) => {
      const items = pmWorkOrders.filter((wo) => {
        const eff = this.getEffectiveDate(wo);
        return eff && eff.getFullYear() === m.year && eff.getMonth() === m.month;
      });
      const completed = items.filter((wo) => this.getPmBucket(wo) === "completed").length;
      const pct = items.length ? (completed / items.length) * 100 : 0;
      return { label: m.label, pct, hasData: items.length > 0 };
    });
  };

  render() {
    const { start_date, end_date } = this.state;
    const filteredPm = this.getFilteredPmWorkOrders();

    const completedCount = filteredPm.filter((wo) => this.getPmBucket(wo) === "completed").length;
    const scheduledCount = filteredPm.filter((wo) => this.getPmBucket(wo) === "scheduled").length;
    const overdueInRangeCount = filteredPm.filter((wo) => this.getPmBucket(wo) === "overdue").length;
    const pendingCount = filteredPm.filter((wo) => this.getPmBucket(wo) === "in_progress").length;
    const totalPm = filteredPm.length;
    const complianceRate = totalPm ? (completedCount / totalPm) * 100 : 0;

    const categoryCounts = this.getCategoryCounts(filteredPm);
    const categoryTotal = categoryCounts.reduce((sum, c) => sum + c.count, 0);
    const donutGradient = this.buildDonutGradient(categoryCounts, categoryTotal);

    const trend = this.getComplianceTrend();
    const trendPoints = trend.map((pt, idx) => ({ x: 5 + idx * 18, y: 100 - pt.pct }));
    const linePath = `M ${trendPoints.map((p) => `${p.x},${p.y}`).join(" L ")}`;
    const areaPath = `M ${trendPoints[0].x},100 L ${trendPoints.map((p) => `${p.x},${p.y}`).join(" L ")} L ${trendPoints[trendPoints.length - 1].x},100 Z`;

    const overduePms = this.getOverdueList();
    const summaryPms = this.getTaskSummaryRows();

    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarAdmin />
        <div className="body p-4 mt-3">

          {/* Header Title */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
            <div>
              <h3 className="fw-bold mb-1">{t("pm_compliance_report")}</h3>
              <div className="text-secondary">{t("track_pm_performance")}</div>
            </div>
            <div className="d-flex gap-3 align-items-center">
              <div style={{ width: "260px" }}>
                <DateRangePicker
                  type="th"
                  className="form-control bg-white rounded-2 border-0 shadow-sm text-dark"
                  value={{ start_date, end_date }}
                  onChange={(range) =>
                    this.setState({
                      start_date: range.start_date,
                      end_date: range.end_date,
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Top KPI Cards (5 Columns) */}
          <div className="row g-3 mb-4">
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: "#fcf8ff" }}>
                <div className="text-secondary text-14 fw-bold mb-1">{t("pm_compliance")}</div>
                <h2 className="fw-bold mb-0" style={{ color: "#a855f7", fontSize: "2.5rem" }}>{complianceRate.toFixed(1)}%</h2>
              </div>
            </div>
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: "#f0fdf4" }}>
                <div className="text-secondary text-14 fw-bold mb-1">{t("completed_pms")}</div>
                <h2 className="fw-bold mb-0" style={{ color: "#22c55e", fontSize: "2.5rem" }}>{completedCount}</h2>
              </div>
            </div>
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: "#eff6ff" }}>
                <div className="text-secondary text-14 fw-bold mb-1">{t("scheduled_pms")}</div>
                <h2 className="fw-bold mb-0" style={{ color: "#3b82f6", fontSize: "2.5rem" }}>{scheduledCount}</h2>
              </div>
            </div>
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: "#fef2f2" }}>
                <div className="text-secondary text-14 fw-bold mb-1">{t("overdue_pms")}</div>
                <h2 className="fw-bold mb-0" style={{ color: "#ef4444", fontSize: "2.5rem" }}>{overdueInRangeCount}</h2>
              </div>
            </div>
            <div className="col">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: "#fffbeb" }}>
                <div className="text-secondary text-14 fw-bold mb-1">{t("pending_pms")}</div>
                <h2 className="fw-bold mb-0" style={{ color: "#f59e0b", fontSize: "2.5rem" }}>{pendingCount}</h2>
              </div>
            </div>
          </div>

          {/* Charts Row (2 Columns) */}
          <div className="row g-4 mb-4">

            {/* PM Compliance Trend (Line Chart via SVG) - ย้อนหลัง 6 เดือนจากใบสั่งงาน PM จริง */}
            <div className="col-lg-6">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 bg-white">
                <h6 className="fw-bold text-dark mb-4">{t("pm_compliance_trend")}</h6>

                <div className="position-relative w-100 pb-4" style={{ height: "220px" }}>
                  <div className="position-absolute top-0 start-0 d-flex flex-column justify-content-between h-100 text-secondary text-10 text-end pe-2" style={{ width: "30px", bottom: "25px" }}>
                    <span>100</span><span>80</span><span>60</span><span>40</span><span>20</span><span>0</span>
                  </div>

                  <div className="position-absolute h-100" style={{ left: "30px", right: "0", bottom: "25px" }}>

                    <div className="w-100 h-100 d-flex flex-column justify-content-between position-absolute top-0 start-0 z-0">
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="border-top w-100" style={{ borderColor: "#f1f5f9" }}></div>
                      ))}
                    </div>

                    <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 100 100" className="position-relative z-1 overflow-visible">
                      <line x1="0" y1="10" x2="100" y2="10" stroke="#ef4444" strokeWidth="1" strokeDasharray="2,2" />
                      <path d={areaPath} fill="rgba(168, 85, 247, 0.1)" />
                      <path d={linePath} fill="none" stroke="#a855f7" strokeWidth="1.5" />
                      {trendPoints.map((p, idx) => (
                        <circle key={idx} cx={p.x} cy={p.y} r="1.5" fill="#fff" stroke="#a855f7" strokeWidth="1" />
                      ))}
                    </svg>

                    <div className="d-flex justify-content-between position-absolute w-100 text-secondary text-12" style={{ bottom: "-25px" }}>
                      {trend.map((pt, idx) => (
                        <span key={idx} style={{ width: "10%", textAlign: "center" }}>{pt.label}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-center gap-4 mt-3 text-12 text-secondary">
                  <div className="d-flex align-items-center">
                    <div className="wpx-30 hpx-10 border border-2 rounded me-2" style={{ borderColor: "#a855f7", backgroundColor: "rgba(168, 85, 247, 0.1)" }}></div>
                    {t("compliance_percent")}
                  </div>
                  <div className="d-flex align-items-center">
                    <div className="wpx-30 hpx-0 border-top border-2 me-2" style={{ borderStyle: "dashed", borderColor: "#ef4444" }}></div>
                    {t("target_90")}
                  </div>
                </div>
              </div>
            </div>

            {/* PM by Asset Category (Donut Chart) - จัดกลุ่มตาม part_category ของ asset ในช่วงวันที่ที่เลือก */}
            <div className="col-lg-6">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 bg-white">
                <h6 className="fw-bold text-dark mb-4">{t("pm_by_asset_category")}</h6>

                <div className="d-flex align-items-center justify-content-center h-100 gap-5 pb-3">
                  <div
                    className="rounded-circle position-relative flex-shrink-0"
                    style={{
                      width: "200px", height: "200px",
                      background: donutGradient
                    }}
                  >
                    <div className="position-absolute top-50 start-50 translate-middle bg-white rounded-circle" style={{ width: "100px", height: "100px" }}></div>
                  </div>

                  <div className="d-flex flex-column gap-3 text-14 text-secondary">
                    {categoryCounts.length > 0 ? (
                      categoryCounts.slice(0, 6).map((c) => (
                        <div className="d-flex align-items-center" key={c.label}>
                          <div className="wpx-25 hpx-12 rounded me-3" style={{ backgroundColor: c.color }}></div>
                          {c.label} ({c.count})
                        </div>
                      ))
                    ) : (
                      <div>{t("no_data")}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Overdue PMs Table */}
          <div className="card border-0 rounded-4 shadow-sm mb-4 overflow-hidden bg-white">
            <div className="p-3 border-bottom d-flex align-items-center" style={{ backgroundColor: "#fef2f2" }}>
              <span className="icon text-danger me-2 text-16">{""}</span>
              <h6 className="fw-bold text-danger mb-0">{t("overdue_pms")}</h6>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ backgroundColor: "#fef2f2" }}>
                  <tr className="text-dark fw-bold text-14">
                    <th className="py-3 px-4 border-0">{t("pm_task")}</th>
                    <th className="py-3 border-0">{t("asset")}</th>
                    <th className="py-3 border-0">{t("due_date")}</th>
                    <th className="py-3 border-0">{t("days_overdue")}</th>
                    <th className="py-3 border-0">{t("assigned_to")}</th>
                    <th className="py-3 border-0">{t("action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {overduePms.length > 0 ? (
                    overduePms.map((item) => (
                      <tr key={item.uid} className="border-bottom bg-white">
                        <td className="py-3 px-4 text-dark text-14">{item.task}</td>
                        <td className="text-dark text-14">{item.asset}</td>
                        <td className="text-dark text-14">{item.due}</td>
                        <td className="fw-bold text-danger text-14">{item.daysOverdue} {t("days_overdue")}</td>
                        <td className="text-dark text-14">{item.tech}</td>
                        <td>
                          <a href="#" className="text-primary text-decoration-none text-14">{t("reschedule")}</a>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-secondary py-4">{t("no_data")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* PM Tasks Summary Table */}
          <div className="card border-0 rounded-4 shadow-sm mb-4 overflow-hidden bg-white">
            <div className="p-4 border-bottom">
              <h6 className="fw-bold text-dark mb-0">{t("pm_tasks_summary")}</h6>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr className="text-dark fw-bold text-14">
                    <th className="py-3 px-4 border-0">{t("pm_task")}</th>
                    <th className="py-3 border-0">{t("frequency")}</th>
                    <th className="py-3 border-0">{t("asset_category")}</th>
                    <th className="py-3 border-0">{t("last_completed")}</th>
                    <th className="py-3 border-0">{t("next_due")}</th>
                    <th className="py-3 border-0">{t("status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryPms.length > 0 ? (
                    summaryPms.map((item) => (
                      <tr key={item.uid} className="border-bottom bg-white">
                        <td className="py-3 px-4 text-dark text-14">{item.task}</td>
                        <td className="text-dark text-14">{item.frequency}</td>
                        <td className="text-dark text-14">{item.category}</td>
                        <td className="text-dark text-14">{item.last}</td>
                        <td className="text-dark text-14">{item.next}</td>
                        <td>
                          <span className={`badge rounded-pill fw-bold text-12 px-3 py-1 border ${item.statusClass}`}>
                            {t(item.status)}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-secondary py-4">{t("no_data")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    );
  }
}
